import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { AccountCoupon, useAccountCoupons } from '@/data/Content/AccountCoupons';
import { GUEST_REPURCHASE_COUPON_KEY } from '@/data/Content/AccountCouponsStorage';
import { useServerSideRuntime } from '@/data/context/origin';
import { OrderAdjustment } from '@/data/types/Order';
import type { DppCouponSource } from '@/data/dpp/types';

const MAX_REPURCHASE_PERCENTAGE = 50;
export const REPURCHASE_PROMOTION_PATTERN = /^DPP-(\d+)-\d+D$/i;

type StoredRepurchaseCouponStatus = 'AVAILABLE' | 'APPLIED';

export type StoredRepurchaseCoupon = {
	promotionId: string;
	promotionName?: string;
	percentage: number;
	expirationDateTime?: string;
	status: StoredRepurchaseCouponStatus;
};

type RepurchaseLink = {
	promotionId: string;
	percentage: number;
	expirationDateTime?: string;
	activateOnLogin?: boolean;
	activationSource?: RepurchaseActivationSource;
};

type RepurchaseTarget = {
	promotionName?: string;
	percentage?: number;
};

export type RepurchaseActivationSource = DppCouponSource;

type Listener = () => void;

const listeners = new Set<Listener>();
let snapshotRaw: string | null = null;
let snapshotValue: RepurchaseLink | undefined;
let activatingPending = false;
let activationSource: RepurchaseActivationSource | null = null;

const readRaw = () => {
	try {
		return globalThis.sessionStorage?.getItem(GUEST_REPURCHASE_COUPON_KEY) ?? null;
	} catch {
		return null;
	}
};

export const isValidRepurchasePercentage = (value?: number): value is number =>
	Number.isInteger(value) &&
	(value as number) > 0 &&
	(value as number) <= MAX_REPURCHASE_PERCENTAGE;

const parseLink = (raw: string | null) => {
	try {
		const link = raw ? (JSON.parse(raw) as RepurchaseLink) : undefined;
		return link?.promotionId && isValidRepurchasePercentage(link.percentage) ? link : undefined;
	} catch {
		return undefined;
	}
};

const getSnapshot = () => {
	const raw = readRaw();
	if (raw !== snapshotRaw) {
		snapshotRaw = raw;
		snapshotValue = parseLink(raw);
	}
	return snapshotValue;
};

const writeLink = (link: RepurchaseLink) => {
	try {
		globalThis.sessionStorage?.setItem(GUEST_REPURCHASE_COUPON_KEY, JSON.stringify(link));
	} catch {
		return;
	}
	listeners.forEach((listener) => listener());
};

const setActivateOnLogin = (activateOnLogin: boolean) => {
	const current = getSnapshot();
	if (current && !!current.activateOnLogin !== activateOnLogin) {
		writeLink({ ...current, activateOnLogin });
	}
};

export const requestActivationOnLogin = (source: RepurchaseActivationSource) => {
	const current = getSnapshot();
	if (current) {
		writeLink({ ...current, activateOnLogin: true, activationSource: source });
	}
};

export const cancelActivationOnLogin = () => setActivateOnLogin(false);

const readActivationSource = () => activationSource;

export const setActivationSource = (source: RepurchaseActivationSource) => {
	activationSource = source;
	listeners.forEach((listener) => listener());
};

const subscribe = (listener: Listener) => {
	listeners.add(listener);
	return () => {
		listeners.delete(listener);
	};
};

const useRepurchaseLink = () => useSyncExternalStore(subscribe, getSnapshot, () => undefined);

export const useActivationOnLoginRequested = () => !!useRepurchaseLink()?.activateOnLogin;

export const useActivatedFromCart = () =>
	useSyncExternalStore(subscribe, readActivationSource, () => null) === 'cart';

const isExpired = (expirationDateTime?: string) =>
	!!expirationDateTime && new Date(expirationDateTime).getTime() < Date.now();

const findRepurchaseCoupon = (coupons: AccountCoupon[] | undefined, target: RepurchaseTarget) =>
	coupons?.find(
		({ promotionName }) => !!target.promotionName && promotionName === target.promotionName
	) ??
	coupons?.find(
		(coupon) =>
			coupon.kind === 'REPURCHASE' &&
			isValidRepurchasePercentage(coupon.percentage) &&
			(!target.percentage || coupon.percentage === target.percentage)
	);

const useRepurchaseTarget = (link?: RepurchaseLink): RepurchaseTarget => {
	const { personalization } = useServerSideRuntime();
	const promotionId = personalization?.promotionId;
	const linkPercentage = personalization?.percentage;
	const fallbackName = link?.promotionId;
	const fallbackPercentage = link?.percentage;

	return useMemo(
		() =>
			isValidRepurchasePercentage(linkPercentage)
				? { promotionName: promotionId, percentage: linkPercentage }
				: { promotionName: fallbackName, percentage: fallbackPercentage },
		[promotionId, linkPercentage, fallbackName, fallbackPercentage]
	);
};

const fromAccountCoupon = (
	coupons: AccountCoupon[] | undefined,
	target: RepurchaseTarget
): StoredRepurchaseCoupon | undefined => {
	const coupon = findRepurchaseCoupon(coupons, target);
	const percentage = target.percentage ?? coupon?.percentage;
	return coupon && isValidRepurchasePercentage(percentage)
		? {
				promotionId: coupon.promotionId ?? coupon.id,
				promotionName: coupon.promotionName,
				percentage,
				expirationDateTime: coupon.expirationDateTime,
				status: coupon.status,
		  }
		: undefined;
};

const fromStoredLink = (link?: RepurchaseLink): StoredRepurchaseCoupon | undefined =>
	link
		? {
				promotionId: link.promotionId,
				promotionName: link.promotionId,
				percentage: link.percentage,
				expirationDateTime: link.expirationDateTime,
				status: 'AVAILABLE',
		  }
		: undefined;

export const useStoredRepurchaseCoupon = () => {
	const link = useRepurchaseLink();
	const { userId, coupons } = useAccountCoupons();
	const target = useRepurchaseTarget(link);

	const coupon = userId ? fromAccountCoupon(coupons, target) : fromStoredLink(link);
	return coupon && !isExpired(coupon.expirationDateTime) ? coupon : undefined;
};

export const useRepurchaseCouponActions = () => {
	const link = useRepurchaseLink();
	const { userId, coupons, activate } = useAccountCoupons();
	const target = useRepurchaseTarget(link);

	const registerCoupon = useCallback((incoming: Omit<RepurchaseLink, 'activateOnLogin'>) => {
		if (!isValidRepurchasePercentage(incoming.percentage)) {
			return;
		}
		const current = getSnapshot();
		const activateOnLogin =
			current?.promotionId === incoming.promotionId && !!current.activateOnLogin;
		writeLink({ ...incoming, activateOnLogin });
	}, []);

	const applyCoupon = useCallback(() => {
		if (!userId) {
			return;
		}
		const accountCoupon = findRepurchaseCoupon(coupons, target);
		if (accountCoupon && accountCoupon.status !== 'APPLIED') {
			activate(accountCoupon.id, activationSource ?? undefined).catch(() => undefined);
		}
	}, [userId, coupons, target, activate]);

	return { registerCoupon, applyCoupon };
};

export const useActivatePendingRepurchaseCoupon = () => {
	const link = useRepurchaseLink();
	const { userId, coupons, activate } = useAccountCoupons();
	const target = useRepurchaseTarget(link);

	useEffect(() => {
		if (!userId || !coupons || !link?.activateOnLogin || activatingPending) {
			return;
		}
		activatingPending = true;
		const match = findRepurchaseCoupon(coupons, target);
		(match && match.status !== 'APPLIED'
			? activate(match.id, link.activationSource)
			: Promise.resolve()
		)
			.catch(() => undefined)
			.finally(() => {
				cancelActivationOnLogin();
				activatingPending = false;
			});
	}, [userId, coupons, link, target, activate]);
};

export const getRepurchaseSavings = (adjustment: OrderAdjustment[], promotionName?: string) =>
	promotionName
		? adjustment
				.filter(({ code }) => code === promotionName)
				.reduce((total, { amount }) => total + Math.abs(Number(amount) || 0), 0)
		: 0;

export const useCouponsWithoutRepurchase = (
	coupons: string | number,
	adjustment: OrderAdjustment[] = []
) => {
	const promotionName = useStoredRepurchaseCoupon()?.promotionName;
	const repurchaseSavings = adjustment
		.filter(
			({ code, displayLevel }) =>
				displayLevel === 'Order' &&
				((!!promotionName && code === promotionName) ||
					REPURCHASE_PROMOTION_PATTERN.test(code ?? ''))
		)
		.reduce((total, { amount }) => total + Math.abs(Number(amount) || 0), 0);
	return Math.max(Math.round((Number(coupons) - repurchaseSavings) * 100) / 100, 0);
};

export const applyRepurchaseDiscount = (amount: number, percentage: number) =>
	isValidRepurchasePercentage(percentage) ? amount - Math.round(amount * percentage) / 100 : amount;
